import { Edit } from '@vivaldis/icons'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Typography
} from '@vivaldis/antd-ui'
import { Divider } from '../../../../../../../Divider'
import { ImageUpload } from '../../../../../../components/ImageUpload'
import { Employee } from '../../../../../../types/Employee'
import { useForm } from '../../../../provider/FormProvider'
import { CountryType } from '../../../../../../types/CountryType'
import styled from '@emotion/styled'

interface Props {
  countries: CountryType[]
  employee: Employee
}

export const WorkPermit = ({ countries, employee }: Props) => {
  const [t] = useTranslation('web_ui')

  const [isAddWorkPermitButtonClicked, setIsAddWorkPermitButtonClicked] =
    useState(false)

  const { values, setFieldError, setFieldTouched, setFieldValue } = useForm()

  // we show ID CARD when user selected `eeaMember` country or when country is not selected yet
  const isEeaMember = useMemo(() => {
    if (!values.nationalityCountryId) {
      return true
    }

    return !!countries.find(
      country => country.id === values.nationalityCountryId
    )?.eeaMember
  }, [countries, values.nationalityCountryId])

  const isEmployeeAlreadyHaveWorkPermit = useMemo(() => {
    return (
      employee.workProfile?.workPermitBackPicture ||
      employee.workProfile?.workPermitFrontPicture ||
      employee.workProfile?.workPermitExpiresOn
    )
  }, [
    employee.workProfile?.workPermitBackPicture,
    employee.workProfile?.workPermitExpiresOn,
    employee.workProfile?.workPermitFrontPicture
  ])

  const showAddWorkPermitButton = useMemo(() => {
    return !isAddWorkPermitButtonClicked && !isEmployeeAlreadyHaveWorkPermit
  }, [isAddWorkPermitButtonClicked, isEmployeeAlreadyHaveWorkPermit])

  const showWorkPermitFields = useMemo(() => {
    return isEmployeeAlreadyHaveWorkPermit || isAddWorkPermitButtonClicked
  }, [isAddWorkPermitButtonClicked, isEmployeeAlreadyHaveWorkPermit])

  const handleAddWorkPermitButtonClick = useCallback(() => {
    setIsAddWorkPermitButtonClicked(true)
  }, [])

  if (!values.nationalityCountryId) {
    return null
  }

  if (isEeaMember) {
    return null
  }

  return (
    <>
      <Divider sectionSeparator />
      <Base>
        <Typography.Title level={5} marginBottom={16}>
          {t(
            'edit_employee_personal_information_form.steps.edit_manually.work_permit_section_header'
          )}
        </Typography.Title>
        {showAddWorkPermitButton ? (
          <Button
            onClick={handleAddWorkPermitButtonClick}
            type="default"
            size="middle"
            icon={<Edit />}
          >
            {t(
              'edit_employee_personal_information_form.steps.ocr_succeeded.add_work_permit_button'
            )}
          </Button>
        ) : null}
      </Base>
      {showWorkPermitFields ? (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="workPermitFrontPictureFile"
                label={t(
                  'edit_employee_personal_information_form.steps.edit_manually.work_permit_front_picture.label'
                )}
              >
                <ImageUpload
                  defaultSrc={employee.workProfile?.workPermitFrontPicture}
                  fieldName={'workPermitFrontPictureFile'}
                  value={values.workPermitFrontPictureFile}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="workPermitBackPictureFile"
                label={t(
                  'edit_employee_personal_information_form.steps.edit_manually.work_permit_back_picture.label'
                )}
              >
                <ImageUpload
                  defaultSrc={employee.workProfile?.workPermitBackPicture}
                  fieldName={'workPermitBackPictureFile'}
                  value={values.workPermitBackPictureFile}
                  setFieldError={setFieldError}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="workPermitExpiresOn"
            label={t(
              'edit_employee_personal_information_form.steps.edit_manually.work_permit_expires_on.label'
            )}
          >
            <DatePicker
              name="workPermitExpiresOn"
              format="dd-MM-yyyy"
              placeholder={t('form.required')}
              style={{ width: '100%' }}
              autoComplete="off"
            />
          </Form.Item>
        </>
      ) : null}
    </>
  )
}

const Base = styled('div')`
  display: flex;
  justify-content: space-between;
`
